import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/do';
import { ApiConnectService } from './api-connect.service';
import { OffboardingArchivePage, OffboardingAssignment, OffboardingCase, OffboardingCreate,
  OffboardingMutation, OffboardingOptions, OffboardingPage, OffboardingPermissions,
  OffboardingHistoryPage, OffboardingImportAuditPage,
  OffboardingAssigneeContext, OffboardingImportJob, OffboardingQueue } from '../models/offboarding.model';
import { OffboardingEntry, OffboardingReturnPermissions, OffboardingReturnClothingPage } from '../models/offboarding.model';
import { OFFBOARDING as O } from '../content-page/offboarding/offboarding.constants';
import { ASSET } from '../content-page/common/employee-assets/asset.constants';
import { AssetAction } from '../models/employee-asset.model';
import { ClothingTypeReturnRequest } from '../models/clothing-disposition.model';
import { PlanfixOffboardingEntry, PlanfixOffboardingLaunch, PlanfixOffboardingRequest } from '../models/planfix-offboarding.model';

const PLANFIX = {base: '/planfix', launches: '/launches'};

@Injectable()
export class OffboardingService {
  private changes = new Subject<void>();
  readonly caseChanges: Observable<void> = this.changes.asObservable();
  constructor(private http: HttpClient, private api: ApiConnectService) {}
  permissions(): Observable<OffboardingPermissions> {
    return this.http.get<OffboardingPermissions>(this.base() + O.permissions);
  }
  options(query: string, context?: OffboardingAssigneeContext): Observable<OffboardingOptions> {
    let params = new HttpParams().set(O.query, query);
    if (context) { params = this.contextParams(params, context); }
    return this.http.get<OffboardingOptions>(this.base() + O.options,
      {params: params});
  }
  employeeCases(id: string): Observable<OffboardingCase[]> {
    return this.http.get<OffboardingCase[]>(this.base() + O.employees + encodeURIComponent(id) + O.cases);
  }
  search(offset: number, queue?: OffboardingQueue, limit = O.limitValue): Observable<OffboardingPage> {
    const request: {offset: number; limit: number; queue?: OffboardingQueue} = {offset: offset, limit: limit};
    if (queue) { request.queue = queue; }
    return this.http.post<OffboardingPage>(this.base() + O.search, request);
  }
  get(id: string): Observable<OffboardingCase> {
    return this.http.get<OffboardingCase>(this.base() + O.cases + ASSET.slash + encodeURIComponent(id));
  }
  history(id: string, offset: number): Observable<OffboardingHistoryPage> {
    const params = new HttpParams().set(O.offset, String(offset)).set(O.limit, String(O.limitValue));
    return this.http.get<OffboardingHistoryPage>(this.base() + O.cases + ASSET.slash + id + ASSET.history, {params: params});
  }
  create(request: OffboardingCreate): Observable<OffboardingCase> {
    return this.http.post<OffboardingCase>(this.base() + O.cases, request).do(() => this.changes.next());
  }
  act(id: string, request: OffboardingMutation): Observable<OffboardingCase> {
    return this.http.post<OffboardingCase>(this.base() + O.cases + ASSET.slash + id + O.actions, request).do(() => this.changes.next());
  }
  archive(offset: number): Observable<OffboardingArchivePage> {
    const params = new HttpParams().set(O.offset, String(offset)).set(O.limit, String(O.limitValue));
    return this.http.get<OffboardingArchivePage>(this.base() + O.archive, {params: params});
  }
  receiveAsset(id: string, key: string, request: AssetAction): Observable<OffboardingCase> {
    return this.http.post<OffboardingCase>(this.collectionPath(id, key) + O.assetReceipt, request);
  }
  receiveClothing(id: string, key: string, request: ClothingTypeReturnRequest): Observable<OffboardingCase> {
    return this.http.post<OffboardingCase>(this.collectionPath(id, key) + O.clothingReceipt, request);
  }
  entry(id: string): Observable<OffboardingEntry> {
    return this.http.get<OffboardingEntry>(this.base() + O.employees + encodeURIComponent(id) + O.entry);
  }
  planfixEntry(id: string): Observable<PlanfixOffboardingEntry> {
    return this.http.get<PlanfixOffboardingEntry>(this.base() + PLANFIX.base + O.employees + encodeURIComponent(id) + O.entry);
  }
  launchPlanfix(request: PlanfixOffboardingRequest): Observable<PlanfixOffboardingLaunch> {
    return this.http.post<PlanfixOffboardingLaunch>(this.base() + PLANFIX.base + PLANFIX.launches, request);
  }
  returnPermissions(id: string): Observable<OffboardingReturnPermissions> {
    return this.http.get<OffboardingReturnPermissions>(this.base() + O.employees + encodeURIComponent(id) + O.returnPermissions);
  }
  returnClothing(id: string, page: number): Observable<OffboardingReturnClothingPage> {
    return this.http.get<OffboardingReturnClothingPage>(this.base() + O.employees + encodeURIComponent(id) + O.returnClothing,
      {params: new HttpParams().set(ASSET.page, String(page))});
  }
  importAll(): Observable<OffboardingImportJob> {
    return this.http.post<OffboardingImportJob>(this.base() + O.importAll, {});
  }
  importStatus(): Observable<OffboardingImportJob> {
    return this.http.get<OffboardingImportJob>(this.base() + O.importAll);
  }
  importAudits(offset: number): Observable<OffboardingImportAuditPage> {
    const params = new HttpParams().set(O.offset, String(offset)).set(O.limit, String(O.limitValue));
    return this.http.get<OffboardingImportAuditPage>(this.base() + O.importAudits, {params: params});
  }
  assignments(locationCode?: string): Observable<OffboardingAssignment[]> {
    const params = locationCode ? new HttpParams().set(O.locationCode, locationCode) : new HttpParams();
    return this.http.get<OffboardingAssignment[]>(this.base() + O.assignments, {params: params});
  }
  assign(request: OffboardingAssignment): Observable<OffboardingAssignment> {
    return this.http.post<OffboardingAssignment>(this.base() + O.assignments, request);
  }
  replace(id: string, request: OffboardingAssignment): Observable<OffboardingAssignment> {
    return this.http.post<OffboardingAssignment>(this.base() + O.assignments + ASSET.slash + id + O.replace, request);
  }
  private contextParams(params: HttpParams, context: OffboardingAssigneeContext): HttpParams {
    if (context.targetEmployeeId) { params = params.set(O.targetEmployeeId, context.targetEmployeeId); }
    if (context.function) { params = params.set(O.function, context.function); }
    if (context.caseId) { params = params.set(O.caseId, context.caseId); }
    if (context.assignmentId) { params = params.set(O.assignmentId, context.assignmentId); }
    if (context.successors) { params = params.set(O.successors, String(true)); }
    if (context.start) { params = params.set(O.start, String(true)); }
    return params;
  }
  revoke(id: string, request: OffboardingMutation): Observable<OffboardingAssignment> {
    return this.http.post<OffboardingAssignment>(this.base() + O.assignments + ASSET.slash + id + O.revoke, request);
  }
  private base(): string { return this.api.baseApiUrl + O.base; }
  private collectionPath(id: string, key: string): string {
    return this.base() + O.cases + ASSET.slash + encodeURIComponent(id) + O.collectionItems + encodeURIComponent(key);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/offboarding.service.ts